// Citation + navigation markers (docs/design/2026-07-17-agent-panel.md Task 6;
// wave-2 goto in 2026-07-17-agent-widget-wave2.md N1). The agent emits
// [ref:type:id] (a citation chip) and [goto:type:id|Label] (a navigation
// action). This is the ONLY place markers resolve to routes, so untrusted model
// output can never produce a raw path or a broken link — unknown types/pages
// degrade to plain text.

import { useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Icon } from "../design/ui";
import { getThought, type ThoughtDetail } from "../lib/brain-api";
import "./agent.css";

const REF_RE = /\[ref:([a-z_]+):([^\]\s]+)\]/g;
// goto id runs to the first `|` (optional caption) or `]`; captions are free text.
const GOTO_RE = /\[goto:([a-z_]+):([^\]|]+)(?:\|([^\]]+))?\]/g;

export type RefSegment =
  | { kind: "text"; text: string }
  | { kind: "ref"; type: string; id: string };

/** Split text into plain-text and ref segments. Pure + exported so the MUI
 *  CitationChip path (mui/AgentText) shares this one parser. */
export function parseRefs(text: string): RefSegment[] {
  const out: RefSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(REF_RE)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: "text", text: text.slice(last, at) });
    out.push({ kind: "ref", type: m[1], id: m[2] });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** Composite "<parent>/<child>" id → a tab+row deep link on the parent's page. */
function rowRoute(base: string, id: string, tab: string, rowParam: string): string | null {
  const [parent, rowId] = id.split("/");
  if (!parent) return null;
  const q = new URLSearchParams({ tab });
  if (rowId) q.set(rowParam, rowId);
  return `/${base}/${encodeURIComponent(parent)}?${q.toString()}`;
}

// The single source of ref→route truth. null = render a chip with no navigation.
export const REF_ROUTES: Record<string, (id: string) => string | null> = {
  customer: (id) => `/customers/${encodeURIComponent(id)}`,
  project: (id) => `/projects/${encodeURIComponent(id)}`,
  // id is "<customerId>/<visitId>" — deep-link opens the call report (N3).
  visit: (id) => rowRoute("customers", id, "visits", "visit"),
  action: (id) => rowRoute("projects", id, "actions", "action"),
  followup: (id) => rowRoute("projects", id, "overview", "followup"),
  worklog: (id) => rowRoute("customers", id, "log", "row"),
  proposal: (id) => rowRoute("projects", id, "plan", "proposal"),
  integration: (id) => rowRoute("customers", id, "integrations", "integration"),
  person: (id) => `/people/${encodeURIComponent(id)}`,
  standup: (id) => `/standup/history/${encodeURIComponent(id)}`,
  ticket: () => null, // external — see refExternalHref
  thought: () => null, // no thought detail page yet — non-link chip, id in tooltip
};

const JIRA_BROWSE = "https://aeronetnz.atlassian.net/browse";

/** External URL for a ref, or null. Kept beside REF_ROUTES so both chip
 *  renderers (RefChip + mui/CitationChip) share the one resolution table. */
export function refExternalHref(type: string, id: string): string | null {
  return type === "ticket" && /^[A-Z][A-Z0-9]+-\d+$/.test(id) ? `${JIRA_BROWSE}/${id}` : null;
}

// Static-page allowlist for [goto:page:<key>]. The agent can navigate anywhere in
// the app, but only through this fixed set — never a model-supplied path.
const PAGE_ROUTES: Record<string, { path: string; label: string }> = {
  today: { path: "/today", label: "Today" },
  customers: { path: "/customers", label: "Customers" },
  projects: { path: "/projects", label: "Projects" },
  standup: { path: "/standup", label: "Standup" },
  finance: { path: "/finance", label: "Finance" },
  atlas: { path: "/atlas", label: "Atlas" },
  cockpit: { path: "/cockpit", label: "Cockpit" },
  "brain-health": { path: "/brain-health", label: "Brain health" },
  feed: { path: "/feed-bus", label: "Feed" },
  agents: { path: "/agents", label: "Agents" },
  settings: { path: "/settings", label: "Settings" },
};

const REF_ICON: Record<string, string> = {
  customer: "building",
  project: "box",
  visit: "map-pin",
  ticket: "ticket",
  thought: "bulb",
  page: "layout-grid",
  action: "square-check",
  followup: "corner-down-right",
  worklog: "clock",
  proposal: "git-branch",
  integration: "plug",
  person: "user",
  standup: "sunrise",
};

// Composite "<parent>/<child>" ids label as the child row's short id.
const COMPOSITE = new Set(["visit", "action", "followup", "worklog", "proposal", "integration"]);

/** Short, human label for a chip. */
export function refLabel(type: string, id: string): string {
  if (COMPOSITE.has(type)) return id.split("/")[1] ?? id;
  if (type === "thought") return "note";
  return id;
}

/** Resolve a goto marker to an in-app path, or null to degrade to text. */
function resolveGoto(type: string, id: string): string | null {
  if (type === "page") return PAGE_ROUTES[id]?.path ?? null;
  return REF_ROUTES[type]?.(id) ?? null;
}

/** Router state so the destination page can flash the arrived record (N2). */
function focusFor(type: string, id: string) {
  return type === "page" ? undefined : { agentFocus: { type, id } };
}

/** thought: chips open a read-only drawer dialog — no thought detail page
 *  exists, and one dialog beats a whole route (system-admin design §2.1). */
function ThoughtChip({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const [thought, setThought] = useState<ThoughtDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const show = () => {
    setOpen(true);
    if (!thought && !error) {
      getThought(id).then(setThought).catch((e) => setError(e instanceof Error ? e.message : String(e)));
    }
  };
  return (
    <>
      <button type="button" className="agent-ref agent-ref--link" title={`thought:${id}`} onClick={show}>
        <Icon name="bulb" size={12} />
        note
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          {thought ? `${thought.type ?? "thought"}${thought.display_name ? ` · ${thought.display_name}` : ""}` : "Thought"}
        </DialogTitle>
        <DialogContent>
          {error && <div className="agent-error">{error}</div>}
          {!thought && !error && <div style={{ fontSize: 13, opacity: 0.7 }}>Loading…</div>}
          {thought && (
            <>
              <div style={{ fontSize: 14, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{thought.content}</div>
              <div style={{ fontSize: 11.5, opacity: 0.65, marginTop: 12, fontFamily: "'DM Mono', monospace" }}>
                {[
                  thought.scope,
                  thought.topics?.join(", "),
                  thought.created_at ? new Date(thought.created_at).toLocaleDateString("en-NZ") : null,
                ].filter(Boolean).join(" · ")}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function RefChip({ type, id }: { type: string; id: string }) {
  if (type === "thought") return <ThoughtChip id={id} />;
  const route = REF_ROUTES[type];
  // Truly unknown type → plain text passthrough, never a broken link.
  if (!route) return <>{id}</>;
  const href = route(id);
  const icon = REF_ICON[type] ?? "link";
  const label = refLabel(type, id);
  const title = `${type}:${id}`;
  const external = refExternalHref(type, id);
  if (external) {
    return (
      <a
        className="agent-ref agent-ref--link"
        href={external}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
      >
        <Icon name={icon} size={12} />
        {label}
      </a>
    );
  }
  if (href) {
    return (
      <Link className="agent-ref agent-ref--link" to={href} title={title} state={focusFor(type, id)}>
        <Icon name={icon} size={12} />
        {label}
      </Link>
    );
  }
  return (
    <span className="agent-ref" title={title}>
      <Icon name={icon} size={12} />
      {label}
    </span>
  );
}

function GotoButton({ type, id, caption }: { type: string; id: string; caption: string }) {
  const href = resolveGoto(type, id);
  if (!href) return <>{caption}</>; // unknown target → plain text
  return (
    <Link className="agent-goto" to={href} state={focusFor(type, id)}>
      <span className="agent-goto-text">
        <Icon name={REF_ICON[type] ?? "arrow-right"} size={14} />
        {caption}
      </span>
      <Icon name="arrow-right" size={15} />
    </Link>
  );
}

/** Caption for a goto button — explicit |Label wins, else derive it. */
function gotoCaption(type: string, id: string, label?: string): string {
  if (label) return label;
  if (type === "page") return PAGE_ROUTES[id]?.label ?? id;
  return refLabel(type, id);
}

// ── Structured answer cards (A1) ─────────────────────────────────────────────
// The model may emit a ```agent-card fenced JSON block; we render it as an
// app-native card. ANY parse/shape failure degrades to a plain code block.

interface CardStat { label: string; value: string | number; }
interface CardRow { text: string; ref?: string; }
interface AgentCardData {
  title: string;
  ref?: string;
  subtitle?: string;
  stats?: CardStat[];
  rows?: CardRow[];
}

function parseCard(raw: string): AgentCardData | null {
  try {
    const c = JSON.parse(raw) as AgentCardData;
    if (c && typeof c.title === "string") return c;
  } catch { /* not valid JSON */ }
  return null;
}

/** Render a "type:id" marker string as a chip (reuses the one ref pipeline). */
function MarkerChip({ marker }: { marker: string }) {
  const i = marker.indexOf(":");
  if (i < 0) return <>{marker}</>;
  return <RefChip type={marker.slice(0, i)} id={marker.slice(i + 1)} />;
}

function AgentCard({ card }: { card: AgentCardData }) {
  return (
    <div className="agent-card">
      <div className="agent-card-head">
        <span className="agent-card-title">{card.title}</span>
        {card.ref && <MarkerChip marker={card.ref} />}
      </div>
      {card.subtitle && <div className="agent-card-sub">{card.subtitle}</div>}
      {card.stats && card.stats.length > 0 && (
        <div className="agent-card-stats">
          {card.stats.map((s, i) => (
            <div key={i} className="agent-card-stat">
              <span className="agent-card-stat-val">{s.value}</span>
              <span className="agent-card-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      )}
      {card.rows && card.rows.length > 0 && (
        <div className="agent-card-rows">
          {card.rows.map((r, i) => (
            <div key={i} className="agent-card-row">
              <span>{r.text}</span>
              {r.ref && <MarkerChip marker={r.ref} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Markdown-rendered assistant output with citation chips + goto buttons. Markers
 *  are rewritten to links with a custom scheme, then intercepted at the `a`
 *  renderer — so they survive inside bold/lists/headings without an AST walk. */
export function RefMarkdown({ text }: { text: string }) {
  const md = text
    .replace(GOTO_RE, (_m, type: string, id: string, label?: string) =>
      `[${gotoCaption(type, id, label)}](goto:${type}:${id})`,
    )
    .replace(REF_RE, (_m, type: string, id: string) => `[${refLabel(type, id)}](ref:${type}:${id})`);
  return (
    <ReactMarkdown
      // The default transform strips unknown protocols — it would erase our
      // custom hrefs and leave dead <a> tags. Let ours through, sanitize the rest.
      urlTransform={(url) =>
        url.startsWith("ref:") || url.startsWith("goto:") ? url : defaultUrlTransform(url)
      }
      components={{
        a: ({ href, children }) => {
          let m = /^ref:([a-z_]+):(.+)$/.exec(href ?? "");
          if (m) return <RefChip type={m[1]} id={m[2]} />;
          m = /^goto:([a-z_]+):(.+)$/.exec(href ?? "");
          if (m) return <GotoButton type={m[1]} id={m[2]} caption={String(children)} />;
          return <a href={href}>{children}</a>;
        },
        // Intercept ```agent-card blocks → native card; anything else stays a <pre>.
        pre: ({ node, children }) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const codeEl: any = (node as any)?.children?.[0];
          const cls = codeEl?.properties?.className;
          if (Array.isArray(cls) && cls.includes("language-agent-card")) {
            const card = parseCard(String(codeEl?.children?.[0]?.value ?? "").trim());
            if (card) return <AgentCard card={card} />;
          }
          // Save-to-brain proposals (W2) are rendered by the widget as an editable
          // panel, not inline — swallow the raw block here.
          if (Array.isArray(cls) && cls.includes("language-agent-proposals")) return null;
          // Mutation proposals (confirm cards) likewise render as a panel.
          if (Array.isArray(cls) && cls.includes("language-agent-action")) return null;
          return <pre>{children}</pre>;
        },
      }}
    >
      {md}
    </ReactMarkdown>
  );
}
